import { useState, useEffect } from 'react';
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";

// Components
import About from "./components/about/About.js";
import AboutHeader from "./components/AboutHeader/AboutHeader.js";
import Contact from "./components/contact/Contact.js";
import Footer from "./components/Footer/Footer.js";
import NewNavBar from "./components/Navbar/NewNavBar.js";
import Topbar from "./components/Topbar/Topbar.js";
import PackagesPage from "./components/PackagesPage/PackagesPage.js";
import UserConsentPage from "./pages/UserConsentPage.js";
import RealShareAdvisor from "./components/RealShareAdvisor.js";
import PageNotFound from "./components/PageNotFound/PageNotFound.js";
import HeroSection from "./components/HeroSection/HeroSection.js";
import BottomNav from "./components/Bottomav/BottomNav.js";
import ComplaintBoardPage from "./components/ComplaintBoard/ComplaintBoardPage.js";
import GrievanceRedressalComponent from "./components/GrievanceRedressalComponent/GrievanceRedressalComponent.js";
import DisclosureComponent from "./components/DisclosureComponent/DisclosureComponent.js";
import TAndC from "./components/tandc/TAndC.js";
import PrivacyPolicy from "./components/privacypolicy/PrivacyPolicy.js";
import RefundPolicy from "./components/refundpolicy/RefundPolicy.js";
import InvestorCharter from "./components/investorcharter/InvestorCharter.js";
import DoDont from "./components/dodont/DoDont.js";
import AdDisclaimer from "./components/addisclaimer/AdDisclaimer.js";
import LegalisclDaimer from "./components/legaldisclaimer/LegalisclDaimer.js";
import AgreementPage from "./pages/AgreementPage";
import StockmantraForm from "./pages/Stockmantraform";
import SubmissionSuccess from "./pages/SubmissionSuccess";
import ThankYou from "./pages/ThankYou";
import InvestorDisclaimerModal from "./components/Investordisclaimermodal/Investordisclaimermodal.jsx";
import AccessibilityWidget from "./components/AccessibilityWidget/AccessibilityWidget.jsx";

// CSS
import "@fancyapps/fancybox/dist/jquery.fancybox.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "owl.carousel/dist/assets/owl.carousel.min.css";
import "owl.carousel/dist/assets/owl.theme.default.min.css";
import "wowjs/css/libs/animate.css";
import "./App.css";
import "./css/style.css";
import "./css/custom-bootstrap.scss";
import BeforeFooter from './components/BeforeFooter/BeforeFooter.js';
import NewKyc from './userconsent/NewKyc.jsx';
import AccessibilityStatement from './components/AccessibilityStatement/AccessibilityStatement.js';
import Sitemap from './components/Sitemap/Sitemap.js';

const SKIP_LABEL_STORAGE_KEY = "ob_skip_label_hidden";

const SkipToMainContent = () => {
  const [hideLabel, setHideLabel] = useState(() => {
    try {
      return window.localStorage.getItem(SKIP_LABEL_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setLabelHidden = (hidden) => {
    setHideLabel(hidden);
    try {
      window.localStorage.setItem(SKIP_LABEL_STORAGE_KEY, hidden ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className={`skip-to-main${hideLabel ? " skip-to-main--label-hidden" : ""}`}
    >
      {/* Cross = hide label | Show = show label */}
      <div className="skip-to-main__controls" role="group" aria-label="Skip label visibility">
        {!hideLabel ? (
          <button
            type="button"
            className="skip-to-main__btn skip-to-main__btn--hide"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setLabelHidden(true);
            }}
            aria-label="Hide label text"
            title="Hide text"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="currentColor">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
            <span className="skip-to-main__btn-text">Hide</span>
          </button>
        ) : (
          <button
            type="button"
            className="skip-to-main__btn skip-to-main__btn--show"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setLabelHidden(false);
            }}
            aria-label="Show label text"
            title="Show text"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="currentColor">
              <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
            </svg>
            <span className="skip-to-main__btn-text">Show</span>
          </button>
        )}
      </div>

      <a
        href="#main-content"
        className="skip-to-main-content"
        aria-label="Skip to Main Content"
        title="Skip to Main Content"
      >
        <span className="skip-to-main-content__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M5 3h14v2H5V3zm7 4 5 5h-3v6h-4v-6H7l5-5z" />
          </svg>
        </span>
        <span className="skip-to-main-content__label">Skip to Main Content</span>
      </a>
    </div>
  );
};

const DISCLAIMER_STORAGE_KEY = "investorDisclaimerAcknowledged";

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <MainContent />
      </Router>
    </HelmetProvider>
  );
};

// Define routes that use the AboutHeader
const shouldShowHeader = (pathname) => {
  const routesWithHeader = [
    "/about",
    "/packages",
    "/user-consent",
    "/blogs",
    "/contact",
  ];
  return routesWithHeader.includes(pathname);
};

// Define routes that should NOT show topbar (optional)
const hideTopbarOnRoutes = ["/complaint-board", "/grievance-policy"];

const MainContent = () => {
  const location = useLocation();
  const [showTopbar, setShowTopbar] = useState(true);

  // Show the investor disclaimer once per browser session, on first load
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    if (typeof window === "undefined") return false;
    return !window.sessionStorage.getItem(DISCLAIMER_STORAGE_KEY);
  });

  const handleAcknowledgeDisclaimer = () => {
    window.sessionStorage.setItem(DISCLAIMER_STORAGE_KEY, "true");
    setShowDisclaimer(false);
  };

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > lastScrollY + 10) {
            setShowTopbar(false); // Hide on scroll down
          } else if (window.scrollY < lastScrollY - 10) {
            setShowTopbar(true); // Show on scroll up
          }
          lastScrollY = window.scrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const hideTopbar = hideTopbarOnRoutes.includes(location.pathname);

  return (
    <div className="pt-0"> {/* offset handled per-section */}
      {/* Default/fallback meta tags — overridden by page-level <Helmet> in each route component */}
      <Helmet>
        <title>Option Bricks</title>
        <meta
          name="description"
          content="Option Bricks delivers data-backed research across Nifty, BankNifty, and Sensex to help traders stay ahead of the curve."
        />
      </Helmet>

      {/* Skip to Main Content — floating; text on hover; option to hide text */}
      <SkipToMainContent />

      {/* Investor disclaimer — shows once per session on first visit */}
      <InvestorDisclaimerModal
        show={showDisclaimer}
        onAcknowledge={handleAcknowledgeDisclaimer}
      />

      {/* Conditional Topbar */}
      {!hideTopbar && (
        <div
          className={`transition-transform duration-500 ease-in-out ${
            showTopbar ? "translate-y-0" : "-translate-y-full"
          } fixed top-0 left-0 w-full z-50`}
        >
          <Topbar />
        </div>
      )}

      {/* Navbar below Topbar */}
      <div
        className={`transition-transform duration-500 ease-in-out ${
          showTopbar ? "mt-[45px]" : "mt-0"
        }`}
      >
        <NewNavBar />
      </div>




      {/* Main content region — skip link target */}
      <main id="main-content" tabIndex="-1">
        {/* Conditional About Header */}
        {shouldShowHeader(location.pathname) && <AboutHeader />}

        {/* Routes */}
        <Routes>
          <Route path="/" element={<RealShareAdvisor />} />
          <Route path="/about" element={<About />} />
          {/* <Route path="/user-consent" element={<UserConsentPage />} /> */}
            <Route path="/user-consent" element={<NewKyc />} /> {/*  element={<StockmantraForm />} */}

             <Route path="/success" element={<SubmissionSuccess />} />
          <Route path="/agreement" element={<AgreementPage />} />
            <Route path="/thank-you" element={<ThankYou />} />   {/* NEW */}


          <Route path="/service-offerings" element={<PackagesPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/disclosure" element={<DisclosureComponent />} />
          <Route path="/tandc" element={<TAndC />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/investor-charter" element={<InvestorCharter />} />
          <Route path="/do-dont" element={<DoDont />} />
          <Route path="/ad-disclaimer" element={<AdDisclaimer />} />
          <Route path="/legal-disclaimer" element={<LegalisclDaimer />} />
          <Route path="/complaint-board" element={<ComplaintBoardPage />} />
          <Route path="/grievance-policy" element={<GrievanceRedressalComponent />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </main>

      {/* Common sections */}
      {/* <HeroSection /> */}
      {/* <BeforeFooter/> */}
      <Footer />
      <BottomNav />
      {/* Accessibility Widget — always visible */}
      <AccessibilityWidget />
    </div>
  );
};

export default App;